import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X, Lock, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Navbar = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navbarHeight;

      // Usar animate para un scroll más suave y controlado
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = [
    { name: 'Inicio', id: 'hero' },
    { name: 'Nosotros', id: 'about' },
    { name: 'Tours', id: 'tours' },
    { name: 'Restaurant', id: 'restaurant' },
    { name: 'Vinos', id: 'wines' },
    { name: 'Contacto', id: 'contact' }
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg' 
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.div 
            className="flex items-center cursor-pointer"
            whileHover={{ scale: 1.05 }}
            onClick={() => scrollToSection('hero')}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_vino-manager-2/artifacts/ozsqv7bh_LogoH.svg" 
              alt="Bodega Spinoglio" 
              className="h-12 w-auto"
            />
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <motion.button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`font-medium transition-colors ${
                  isScrolled 
                    ? 'text-gray-600' 
                    : 'text-white'
                }`}
                style={isScrolled ? {} : {}}
                onMouseEnter={(e) => e.target.style.color = '#D4AF37'}
                onMouseLeave={(e) => e.target.style.color = isScrolled ? '#4B5563' : 'white'}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {item.name}
              </motion.button>
            ))}
            <motion.button
              onClick={() => scrollToSection('reservation')}
              className="text-white px-6 py-2 rounded-full font-semibold transition-colors shadow-lg bg-gold"
              style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="nav-reserva-btn"
            >
              Reservar
            </motion.button>
            
            {/* Proposal Access */}
            <motion.button
              onClick={() => navigate('/propuesta')}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="Propuesta Comercial"
            >
              <FileText className={`h-5 w-5 ${isScrolled ? 'text-gray-600' : 'text-white'}`} />
            </motion.button>

            {/* Admin Access */}
            <motion.button
              onClick={() => navigate('/admin/login')}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="Panel Admin"
            >
              <Lock className={`h-5 w-5 ${isScrolled ? 'text-gray-600' : 'text-white'}`} />
            </motion.button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? (
              <X className={`h-6 w-6 ${isScrolled ? 'text-gray-700' : 'text-white'}`} />
            ) : (
              <Menu className={`h-6 w-6 ${isScrolled ? 'text-gray-700' : 'text-white'}`} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden bg-white shadow-lg"
          data-testid="mobile-menu"
        >
          <div className="container mx-auto px-4 py-4 space-y-2">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="block w-full text-left px-4 py-2 text-gray-700 rounded transition-colors"
                onMouseEnter={(e) => {e.target.style.backgroundColor = '#F4E4C1'; e.target.style.color = '#B8941F'}}
                onMouseLeave={(e) => {e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#374151'}}
              >
                {item.name}
              </button>
            ))}
            <button
              onClick={() => scrollToSection('reservation')}
              className="block w-full text-white px-4 py-2 rounded-lg font-semibold transition-colors"
              style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
              data-testid="mobile-reserva-btn"
            >
              Reservar
            </button>
            
            {/* Proposal Access Mobile */}
            <button
              onClick={() => {
                navigate('/propuesta');
                setIsMobileMenuOpen(false);
              }}
              className="block w-full text-left px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <FileText className="h-5 w-5" />
              Propuesta Comercial
            </button>

            {/* Admin Access Mobile */}
            <button
              onClick={() => {
                navigate('/admin/login');
                setIsMobileMenuOpen(false);
              }}
              className="block w-full text-left px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <Lock className="h-5 w-5" />
              Panel Administrativo
            </button>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};

export default Navbar;
